import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Music2, MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { Crest } from "@/components/Crest";
import { getSettings } from "@/lib/db";

const SOCIALS = [
  { key: "facebook", Icon: Facebook, label: "Facebook" },
  { key: "instagram", Icon: Instagram, label: "Instagram" },
  { key: "youtube", Icon: Youtube, label: "YouTube" },
  { key: "tiktok", Icon: Music2, label: "TikTok" },
];

const NAV = [
  ["/", "Αρχική"],
  ["/roster", "Ρόστερ"],
  ["/partners", "Συνεργάτες"],
  ["/contact", "Επικοινωνία"],
];

export function Footer() {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getSettings().then((d) => setSettings(d)).catch(() => {});
  }, []);

  const venues = [
    settings.football_venue_name && { name: settings.football_venue_name, addr: settings.football_venue_address, tag: "Ποδόσφαιρο" },
    settings.basketball_venue_name && { name: settings.basketball_venue_name, addr: settings.basketball_venue_address, tag: "Μπάσκετ" },
  ].filter(Boolean);

  return (
    <footer className="relative overflow-hidden border-t border-gold/25 bg-gradient-to-b from-royal/50 to-navy" data-testid="footer">
      {/* top accent line */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-gold to-transparent" />

      {/* CTA strip */}
      <div className="club-container flex flex-col items-center justify-between gap-4 border-b border-white/10 py-8 text-center sm:flex-row sm:text-left">
        <div>
          <h3 className="font-heading text-2xl font-800 text-white sm:text-3xl">Γίνε μέλος της οικογένειάς μας</h3>
          <p className="mt-1 text-sm text-ice/70">Πάθος, ήθος και ομαδικότητα — στη Δυτική Αττική.</p>
        </div>
        <Link
          to="/contact"
          data-testid="footer-cta-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 font-display text-sm font-700 uppercase tracking-widest text-navy transition-all duration-200 hover:bg-gold/85 hover:shadow-[0_0_30px_rgba(179,144,74,0.6)]"
        >
          Επικοινώνησε μαζί μας
          <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>

      <div className="club-container grid gap-10 py-14 md:grid-cols-12">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <Crest size={60} />
            <span className="font-heading text-2xl font-900 leading-none text-white">
              Ρεάλ Δυτικής<br />Αττικής
            </span>
          </div>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-ice/75">
            {settings.club_description ||
              "Αθλητικός σύλλογος Δυτικής Αττικής — Ποδόσφαιρο & Μπάσκετ. Χτίζουμε αθλητές και χαρακτήρες."}
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ key, Icon, label }) => (
              <a
                key={key}
                href={settings[key] || "#"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${key}`}
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-navy/60 text-ice transition-all duration-200 hover:-translate-y-1 hover:border-gold hover:bg-gold hover:text-navy"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Nav */}
        <div className="md:col-span-2">
          <h4 className="font-display text-sm font-700 uppercase tracking-[0.2em] text-gold">Πλοήγηση</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {NAV.map(([to, label]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="inline-flex items-center gap-1.5 text-ice/75 transition-colors hover:text-gold"
                  data-testid={`footer-link-${label}`}
                >
                  <span className="h-px w-3 bg-gold/50 transition-all duration-200" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-3">
          <h4 className="font-display text-sm font-700 uppercase tracking-[0.2em] text-gold">Επικοινωνία</h4>
          <ul className="mt-4 space-y-3 text-sm text-ice/75">
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-0.5 shrink-0 text-brand" />
              {settings.address || "Αιγάλεω, Δυτική Αττική"}
            </li>
            <li className="flex items-center gap-2.5">
              <Phone size={16} className="shrink-0 text-brand" />
              <a href={`tel:${settings.phone}`} className="hover:text-gold">
                {settings.phone || "6970523934"}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Mail size={16} className="shrink-0 text-brand" />
              <a href={`mailto:${settings.email}`} className="break-all hover:text-gold">
                {settings.email || "info@realdytikisattikis.gr"}
              </a>
            </li>
            {settings.training_hours && (
              <li className="flex items-start gap-2.5">
                <Clock size={16} className="mt-0.5 shrink-0 text-brand" />
                {settings.training_hours}
              </li>
            )}
          </ul>
        </div>

        {/* Venues */}
        <div className="md:col-span-3">
          <h4 className="font-display text-sm font-700 uppercase tracking-[0.2em] text-gold">Έδρες</h4>
          <ul className="mt-4 space-y-4 text-sm text-ice/75">
            {venues.length > 0 ? (
              venues.map((v) => (
                <li key={v.tag}>
                  <span className="font-display text-xs font-700 uppercase tracking-wider text-brand">{v.tag}</span>
                  <p className="font-heading text-base font-700 text-white">{v.name}</p>
                  {v.addr && <p className="text-xs text-ice/60">{v.addr}</p>}
                </li>
              ))
            ) : (
              <li className="text-ice/60">Οι έδρες μας ανακοινώνονται σύντομα.</li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 py-5">
        <div className="club-container flex flex-col items-center justify-between gap-2 text-xs text-ice/55 sm:flex-row">
          <span>© {new Date().getFullYear()} Ρεάλ Δυτικής Αττικής. Με επιφύλαξη παντός δικαιώματος.</span>
          <Link to="/login" className="transition-colors hover:text-gold" data-testid="footer-admin-link">
            Διαχείριση
          </Link>
        </div>
      </div>
    </footer>
  );
}
